import React, { memo, useEffect, useMemo, useState } from 'react';
import {
  Hits,
  SearchBox,
  useInstantSearch,
} from 'react-instantsearch-hooks-web';
import { CTA } from '../../interfaces/CTA.interface';
import { Theme } from '../../interfaces/enums/Theme.enum';
import { rewriteSlug } from '../../utils/RewriteSlug/rewriteSlug.util';
import { withBlokWrapper } from '../BlokWrapper/BlokWrapper';
import HeaderSearchResultHit from '../Header/HeaderSearch/HeaderSearchResultHit';
import Heading, { HeadingTypes } from '../Heading/Heading';
import PageNotFoundSearchResultHit from './PageNotFoundSearchResultHit';
import _ from 'lodash';
import { searchTimeoutQueryHook } from '../../utils/searchTimeoutQueryHook/searchTimeoutQueryHook.util';
import { DataLayerContext } from '../../contexts/DataLayerContext';

interface PageNotFoundSearchProps {
  search_bar_placeholder: string;
  search_view_more_cta: CTA[];
  search_no_results_found_title: string;
  search_no_results_found_body?: string;
}

const PageNotFoundSearch: React.FC<PageNotFoundSearchProps> = ({
  search_bar_placeholder,
  search_view_more_cta,
  search_no_results_found_title,
  search_no_results_found_body = '',
}) => {
  const {
    results: { query, nbHits },
  } = useInstantSearch();

  useEffect(() => {
    setOrigin(window.location.origin);
    setIdForSearchBarInputFormField();
  }, []);

  const configureDynamicText = (
    text: string,
    placeholder: string,
    swapValue: string
  ): string => {
    return text.replaceAll(placeholder, swapValue);
  };

  const [origin, setOrigin] = useState<string>('');

  const configureNoResultsText = (text: string, query: string): string => {
    const placeholder: string = '<searched_value>';
    return configureDynamicText(text, placeholder, `${query}`);
  };

  const noResultsTitle: string = useMemo(() => {
    return configureNoResultsText(search_no_results_found_title, query);
  }, [search_no_results_found_title, query]);

  const noResultsBody: string = useMemo(() => {
    return configureNoResultsText(search_no_results_found_body, query);
  }, [search_no_results_found_body, query]);

  const getViewMoreCTALabel = (text: string, nbHits: number) => {
    const placeholder: string = '<number-of-results>';
    const nbHitsAsString: string = `${nbHits}`;
    const viewMoreCTALabel: string = configureDynamicText(
      text,
      placeholder,
      nbHitsAsString
    );
    return viewMoreCTALabel;
  };

  const setIdForSearchBarInputFormField = (): void => {
    const searchBarInput: Element | null = document.querySelector(
      '.ais-SearchBox-input'
    );
    if (searchBarInput) {
      searchBarInput.setAttribute('name', 'header-search-bar-input');
      searchBarInput.setAttribute('id', 'header-search-bar-input');
    }
  };

  const [userSearchCount, setUserSearchCount] = useState<number>(0);

  useEffect(() => {
    if (nbHits !== 0) setUserSearchCount(userSearchCount + 1);
  }, [nbHits]);

  return (
    <DataLayerContext.Provider
      value={{
        dataLayer: {
          search_term: query,
          search_site: userSearchCount,
        },
      }}
    >
      <div data-theme={Theme.LIGHT} className="bg-[#0B212E] relative">
        <SearchBox
          placeholder={search_bar_placeholder}
          classNames={{
            root: '',
            form: 'w-full',
            input:
              'w-full bg-Transparent py-4 px-4 text-text/body focus:outline-none focus:ring-0 rounded-[6px] border-[#0b212e] focus:border-[#0b212e] indent-6',
            submit: 'hidden',
            submitIcon: 'hidden',
            reset: 'hidden hover:hidden',
            resetIcon: 'hidden hover:hidden',
          }}
          title="search-bar"
          id="search-bar"
          aria-label="search bar"
          queryHook={searchTimeoutQueryHook}
        />
        <div
          className="h-[24px] w-[24px] bg-contain bg-no-repeat bg-[#006FA7] transition-all absolute top-4 left-3"
          style={{
            WebkitMaskImage: "url('/svgs/search-small.svg')",
            maskImage: "url('/svgs/search-small.svg')",
          }}
          aria-label="search"
        />
        {query && (
          <>
            {nbHits > 0 && (
              <div className="overflow-y-auto space-y-6 bg-system/white px-10 pt-4 pb-10 rounded-b-[6px] absolute top-[74px] left-0 right-0 z-20">
                <Hits
                  hitComponent={PageNotFoundSearchResultHit}
                  classNames={{
                    list: 'divide-solid divide-y divide-border/light',
                  }}
                />
                {nbHits > 3 && (
                  <div className="flex items-center justify-center">
                    {search_view_more_cta.map((cta: CTA) => (
                      <a
                        href={`${origin}/${rewriteSlug(
                          cta.link?.cached_url
                        )}/?q=${query}`}
                        className="text-text/body-primary hover:underline"
                      >
                        {getViewMoreCTALabel(cta.label, nbHits)}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
            {nbHits === 0 && (
              <div
                className="flex items-center justify-center pt-10 bg-surface/background rounded-b-[6px] absolute top-14 left-0 right-0 p-10"
                data-theme={Theme.LIGHT}
              >
                <div className="flex flex-col items-center justify-center gap-y-3 text-center">
                  <Heading
                    text={noResultsTitle}
                    headingType={HeadingTypes.h4}
                  />
                  {noResultsBody && (
                    <p className="text-text/body">{noResultsBody}</p>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DataLayerContext.Provider>
  );
};

export default memo(PageNotFoundSearch);
